/**
 * Households by number (1.5.2 — Elijah, 2 Oct 2026: "It's the household that signs up. One record per unit, the head
 * of household; define the household by number, add the names if we know them.")
 *
 * Every tenant record carries a Household size (1–4, default 1) and up to three other names, which may be blank —
 * "we know how many, not always who". Kept on the person's own file (_app/people/<TenantID>.json): no SharePoint
 * column is added. Housekeeping is unchanged (the unit is what is cleaned); events and outings count people, the
 * roster counts households.
 */
import { z } from 'zod';

export const HOUSEHOLD_MAX_SIZE = 4;

export const HouseholdSchema = z
  .object({
    size: z.number().int().min(1).max(HOUSEHOLD_MAX_SIZE),
    members: z.array(z.string().trim().max(60)).max(HOUSEHOLD_MAX_SIZE - 1).default([]),
  })
  .transform((h) => ({ size: h.size, members: h.members.slice(0, h.size - 1) }));
export type HouseholdInput = z.input<typeof HouseholdSchema>;

export interface HouseholdView {
  tenantId: string;
  unit: string;
  /** The head of household's first name (the record's own). */
  head: string;
  size: number;
  /** The other names we know (blank where we know there is someone but not who). */
  members: string[];
  /** For an event (?event=): how many from the household may come, and the event's per-household limit. */
  cap?: number;
  perHousehold?: number;
  /** 26 Oct 2026: how the event counts a household, and the number a new RSVP starts at (the most this household may say). */
  countMode?: 'household' | 'limited';
  defaultCount?: number;
}

/** The names a card shows: the head, then the other names we know (blanks left out). */
export function householdNames(h: Pick<HouseholdView, 'head' | 'members'>): string[] {
  return [h.head, ...h.members].map((x) => x.trim()).filter(Boolean);
}
